import React, { useState, useEffect } from 'react';
import CompanyForm from './CompanyForm';
import ImageUpload from './ImageUpload';
import FoundingForm from '../FoundingInfo/FoundingForm';
import SocialLinks from '../Socials/SocialLinks';
import LocationForm from '../Contact/LocationForm';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '@/config/api';
import { Button } from '@/Components/ui/button';
import { Alert, AlertDescription, Progress } from '@/Components/ui/feedback';
import { Building2, Calendar, Share2, Phone, Check, ChevronLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Step metadata drives the branded stepper (left) and the per-step framing
// (right). `optional` marks a step the user can freely skip.
const STEPS = [
  {
    label: 'Company', description: 'Name, logo & intro', icon: Building2,
    heading: 'Introduce your company',
    subtext: 'Add your logo, name and a short intro — this is how candidates will see you on your profile and job posts.',
  },
  {
    label: 'Founding', description: 'Industry & team', icon: Calendar,
    heading: 'A few company details',
    subtext: 'Industry, team size and vision help candidates understand your organisation at a glance.',
  },
  {
    label: 'Socials', description: 'Online presence', icon: Share2, optional: true,
    heading: 'Add your social profiles',
    subtext: 'Link your social profiles so candidates can learn more about you. Totally optional — skip if you like.',
  },
  {
    label: 'Contact', description: 'How to reach you', icon: Phone,
    heading: 'Contact information',
    subtext: 'How candidates and our team can reach your company.',
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9][0-9\s()-]{6,14}$/;

const normalizeUrl = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = (value) => {
  try {
    const parsed = new URL(normalizeUrl(value));
    return Boolean(parsed.hostname);
  } catch {
    return false;
  }
};

function CompanySetup() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('companysetup')) return 0;
    if (path.includes('foundingsetup')) return 1;
    if (path.includes('socials')) return 2;
    if (path.includes('contact')) return 3;
    return 0;
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Company Info State
  const [companyName, setCompanyName] = useState('');
  const [companyLink, setCompanyLink] = useState('');
  const [aboutUs, setAboutUs] = useState('');

  // Founding Info State
  const [organizationType, setOrganizationType] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [yearOfEstablishment, setYearOfEstablishment] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyVision, setCompanyVision] = useState('');

  // Social Media State
  const [socialLinks, setSocialLinks] = useState([]);

  // Contact State
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [mapLocation, setMapLocation] = useState('');

  // Logo / Banner State
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyBanner, setCompanyBanner] = useState('');

  // Calculate progress based on form completion
  const calculateProgress = () => {
    let completedTabs = 0;
    const totalTabs = 4;

    // Tab 0: Company Info
    if (companyName && companyLink && aboutUs) {
      completedTabs++;
    }

    // Tab 1: Founding Info
    if (organizationType && industryType && teamSize && yearOfEstablishment && companyWebsite && companyVision) {
      completedTabs++;
    }

    // Tab 2: Social Media
    if (socialLinks && socialLinks.length > 0 && socialLinks.some(link => link && link.trim() !== '')) {
      completedTabs++;
    }

    // Tab 3: Contact
    if (phoneNumber && email && mapLocation) {
      completedTabs++;
    }

    return Math.round((completedTabs / totalTabs) * 100);
  };

  // Update progress when form data changes
  useEffect(() => {
    setProgress(calculateProgress());
  }, [companyName, companyLink, aboutUs, organizationType, industryType, teamSize, yearOfEstablishment, companyWebsite, companyVision, socialLinks, phoneNumber, email, mapLocation]);

  // Update active tab when route changes
  useEffect(() => {
    const newActiveTab = getActiveTabFromRoute();
    setActiveTab(newActiveTab);
  }, [location.pathname]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      const companyId = localStorage.getItem('companyAccountId');
      if (!companyId) {
        console.error('Company ID is missing. Redirecting to login.');
        window.location.href = '/hirewithus/login';
        return null;
      }
      if (!companyId) return;

      const company_token = localStorage.getItem('company_token');
      if (!company_token) {
        console.error('Company token is missing. Redirecting to login.');
        window.location.href = '/hirewithus/login';
        return;
      }

      // Debug logs removed — were leaking companyId and partial auth token to console.

      try {
        const response = await axios.get(getApiUrl(`/api/hirewithus/company/details/${companyId}`), {
          headers: { 
            Authorization: `Bearer ${company_token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true,
        });

        const data = response.data;
        // Debug log removed — was leaking company details payload to console.

        // Set Company Info
        setCompanyName(data.companyName || '');
        setCompanyLink(data.companyLink || '');
        setAboutUs(data.aboutUs || '');
        
        // Set Founding Info
        setOrganizationType(data.organizationType || '');
        setIndustryType(data.industryType || '');
        setTeamSize(data.teamSize || '');
        setYearOfEstablishment(data.yearOfEstablishment || '');
        setCompanyWebsite(data.companyWebsite || '');
        setCompanyVision(data.companyVision || '');
        
        // Set Social Links
        setSocialLinks(data.socialLinks || []);
        
        // Set Contact Info
        setCountryCode(data.countryCode || '+91');
        setPhoneNumber(data.phoneNumber || '');
        setEmail(data.email || '');
        setMapLocation(data.mapLocation || '');

        // Set Logo / Banner
        setCompanyLogo(data.companyLogo || '');
        setCompanyBanner(data.companyBanner || '');
      } catch (error) {
        console.error('Failed to fetch company details:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        if (error.response?.status === 401) {
          setErrorMessage('Authentication failed. Please login again.');
          setTimeout(() => {
            window.location.href = '/hirewithus/login';
          }, 2000);
        } else {
          setErrorMessage('Failed to fetch company details. Please try again.');
        }
      }
    };

    fetchCompanyDetails();
  }, []);

  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
    
    // Navigate to the appropriate route
    const routes = [
      '/hirewithus/company/companysetup',
      '/hirewithus/company/foundingsetup',
      '/hirewithus/company/socials',
      '/hirewithus/company/contact'
    ];
    navigate(routes[tabIndex]);
  };

  const handleNext = () => {
    if (activeTab < 3) {
      setActiveTab(activeTab + 1);
      
      // Navigate to the next route
      const routes = [
        '/hirewithus/company/companysetup',
        '/hirewithus/company/foundingsetup',
        '/hirewithus/company/socials',
        '/hirewithus/company/contact'
      ];
      navigate(routes[activeTab + 1]);
    } else {
      navigate('/hirewithus/overview');
    }
  };

  const handlePrevious = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
      
      // Navigate to the previous route
      const routes = [
        '/hirewithus/company/companysetup',
        '/hirewithus/company/foundingsetup',
        '/hirewithus/company/socials',
        '/hirewithus/company/contact'
      ];
      navigate(routes[activeTab - 1]);
    }
  };

  // Skip the current step without saving — progressive onboarding; the user can
  // always complete it later from Settings. (No skip on the final step.)
  const handleSkip = () => {
    setErrorMessage('');
    setSuccessMessage('');
    handleNext();
  };

  // Exit onboarding entirely and go to the dashboard. Onboarding is never a hard
  // wall — profile completeness is nudged later, not forced.
  const handleFinishLater = () => {
    navigate('/hirewithus/overview');
  };

  const validateCurrentTab = () => {
    if (activeTab === 0) {
      if (!companyName.trim() || !companyLink.trim() || !aboutUs.trim()) {
        return 'Please fill in all company info fields.';
      }
      if (companyName.trim().length < 2) {
        return 'Company name must be at least 2 characters.';
      }
      if (companyLink.trim().length < 2) {
        return 'Company link must be at least 2 characters.';
      }
      if (aboutUs.trim().length < 20) {
        return 'About us must be at least 20 characters.';
      }
      return '';
    }

    if (activeTab === 1) {
      if (!organizationType || !industryType || !teamSize || !yearOfEstablishment || !companyWebsite.trim() || !companyVision.trim()) {
        return 'Please fill in all founding info fields.';
      }

      const currentYear = new Date().getFullYear();
      const year = Number(yearOfEstablishment);
      if (!Number.isInteger(year) || year < 1800 || year > currentYear) {
        return `Year of establishment must be between 1800 and ${currentYear}.`;
      }
      if (!isValidUrl(companyWebsite)) {
        return 'Please enter a valid company website URL.';
      }
      if (companyVision.trim().length < 20) {
        return 'Company vision must be at least 20 characters.';
      }
      return '';
    }

    if (activeTab === 2) {
      const trimmedLinks = socialLinks
        .map((link) => (link || '').trim())
        .filter(Boolean);

      if (trimmedLinks.length === 0) {
        return 'Please add at least one social media profile.';
      }
      if (trimmedLinks.some((link) => !isValidUrl(link))) {
        return 'Please enter valid social media profile URLs.';
      }
      return '';
    }

    if (activeTab === 3) {
      if (!phoneNumber.trim() || !email.trim() || !mapLocation.trim()) {
        return 'Please fill in all contact info fields.';
      }
      if (!PHONE_REGEX.test(phoneNumber.trim())) {
        return 'Please enter a valid phone number.';
      }
      if (!EMAIL_REGEX.test(email.trim().toLowerCase())) {
        return 'Please enter a valid email address.';
      }
      if (mapLocation.trim().length < 5) {
        return 'Please enter a valid map location.';
      }
      return '';
    }

    return '';
  };

  // Auth header for every write — the backend now enforces ownership, so a
  // missing token = 401. Sent as Bearer (what the backend's company-write
  // guard reads) with credentials for the session-cookie path too.
  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('company_token') || ''}` },
    withCredentials: true,
  });

  const handleSaveAndNext = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    const companyId = localStorage.getItem('companyAccountId');
    if (!companyId) {
      console.error('Company ID is missing. Redirecting to login.');
      window.location.href = '/hirewithus/login';
      return null;
    }

    try {
      setIsLoading(true);
      const validationError = validateCurrentTab();
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }

      // Save based on current tab
      switch (activeTab) {
        case 0: // Company Info
          await axios.post(getApiUrl('/api/hirewithus/company/details'), {
            companyName: companyName.trim(),
            companyLink: companyLink.trim(),
            aboutUs: aboutUs.trim(),
            companyAccountId: companyId,
          }, authConfig());
          break;

        case 1: // Founding Info
          await axios.post(getApiUrl('/api/hirewithus/company/founding'), {
            organizationType,
            industryType,
            teamSize,
            yearOfEstablishment,
            companyWebsite: normalizeUrl(companyWebsite),
            companyVision: companyVision.trim(),
            companyAccountId: companyId,
          }, authConfig());
          break;

        case 2: // Social Media
          await axios.post(getApiUrl('/api/hirewithus/company/socials'), {
            socialLinks: socialLinks.map((link) => {
              const trimmedLink = (link || '').trim();
              return trimmedLink ? normalizeUrl(trimmedLink) : '';
            }),
            companyAccountId: companyId,
          }, authConfig());
          break;

        case 3: // Contact
          await axios.put(getApiUrl('/api/hirewithus/company/contact'), {
            countryCode,
            phoneNumber: phoneNumber.trim(),
            email: email.trim().toLowerCase(),
            mapLocation: mapLocation.trim(),
            companyAccountId: companyId
          }, authConfig());
          break;

        default:
          setErrorMessage('Invalid tab selected.');
          return;
      }

      setSuccessMessage('Information saved successfully!');
      if (activeTab < 3) {
        handleNext();
      } else {
        navigate('/hirewithus/overview');
      }
    } catch (error) {
      console.error('Error saving information:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorMessage('Your session has expired. Please log in again.');
        setTimeout(() => { window.location.href = '/hirewithus/login'; }, 2000);
      } else {
        setErrorMessage(error.response?.data?.error || 'An error occurred while saving the information.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side image guard — mirrors the server rules so users get an
  // instant, friendly error instead of a round-trip 400/413.
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const validateImage = (file) => {
    if (!file) return 'No file selected.';
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return 'Please choose a PNG, JPG, WEBP, or GIF image.';
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return 'Image is too large — maximum size is 5 MB.';
    }
    return '';
  };

  const uploadImage = async (file, kind) => {
    setErrorMessage('');
    setSuccessMessage('');
    const imgError = validateImage(file);
    if (imgError) { setErrorMessage(imgError); return; }

    const companyId = localStorage.getItem('companyAccountId');
    const token = localStorage.getItem('company_token');
    const formData = new FormData();
    formData.append(kind, file);
    try {
      const response = await axios.post(
        getApiUrl(`/api/hirewithus/company/upload-${kind}/${companyId}`),
        formData,
        { headers: { Authorization: `Bearer ${token || ''}` }, withCredentials: true }
      );
      const urlKey = `${kind}Url`;
      if (response.data?.[urlKey]) {
        const setter = kind === 'logo' ? setCompanyLogo : setCompanyBanner;
        setter(response.data[urlKey].split('/').pop());
        setSuccessMessage(`${kind.charAt(0).toUpperCase() + kind.slice(1)} uploaded successfully!`);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorMessage('Your session has expired. Please log in again.');
        setTimeout(() => { window.location.href = '/hirewithus/login'; }, 2000);
      } else {
        setErrorMessage(error.response?.data?.error || `Failed to upload ${kind}.`);
      }
    }
  };

  const handleLogoUpload = (file) => uploadImage(file, 'logo');
  const handleBannerUpload = (file) => uploadImage(file, 'banner');

  const renderTabContent = () => {
    const tabTitles = [
      "Logo & Banner Image",
      "Company Founding Information",
      "Social Media Profiles",
      "Contact Information"
    ];

    switch (activeTab) {
      case 0:
        return (
          <>
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Logo &amp; banner</h3>
              <ImageUpload
                onUpload={handleLogoUpload}
                companyLogo={companyLogo}
                onBannerUpload={handleBannerUpload}
                companyBanner={companyBanner}
              />
              <div className="border-t border-border pt-4">
                <CompanyForm
                  companyName={companyName}
                  setCompanyName={setCompanyName}
                  companyLink={companyLink}
                  setCompanyLink={setCompanyLink}
                  aboutUs={aboutUs}
                  setAboutUs={setAboutUs}
                />
              </div>
            </div>
          </>
        );
      case 1:
        return (
          <>
            <FoundingForm
              organizationType={organizationType}
              setOrganizationType={setOrganizationType}
              industryType={industryType}
              setIndustryType={setIndustryType}
              teamSize={teamSize}
              setTeamSize={setTeamSize}
              yearOfEstablishment={yearOfEstablishment}
              setYearOfEstablishment={setYearOfEstablishment}
              companyWebsite={companyWebsite}
              setCompanyWebsite={setCompanyWebsite}
              companyVision={companyVision}
              setCompanyVision={setCompanyVision}
            />
          </>
        );
      case 2:
        return (
          <>
            <SocialLinks
              socialLinks={socialLinks}
              setSocialLinks={setSocialLinks}
            />
          </>
        );
      case 3:
        return (
          <>
            <LocationForm
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              email={email}
              setEmail={setEmail}
              mapLocation={mapLocation}
              setMapLocation={setMapLocation}
            />
          </>
        );
      default:
        return null;
    }
  };

  // Per-step completion — drives the stepper's checkmarks.
  const tabCompletion = [
    Boolean(companyName && companyLink && aboutUs),
    Boolean(organizationType && industryType && teamSize && yearOfEstablishment && companyWebsite && companyVision),
    Boolean(socialLinks && socialLinks.some((l) => l && String(l).trim() !== '')),
    Boolean(phoneNumber && email && mapLocation),
  ];
  const isLast = activeTab === STEPS.length - 1;

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* ─── Left: branded panel + vertical stepper (desktop) ─── */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/85 p-10 text-primary-foreground lg:flex lg:w-[38%] xl:w-[33%] xl:p-12">
        {/* soft decorative glows */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/5 blur-3xl" />

        <div className="relative z-10">
          <div className="mb-9">
            <img
              src="/Newzenalphablue.png"
              alt="SearchJob.ai"
              className="h-7 w-auto brightness-0 invert"
            />
          </div>
          <h1 className="text-2xl font-bold leading-tight xl:text-[28px]">
            Let&rsquo;s set up your company profile
          </h1>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-primary-foreground/80">
            A complete profile builds trust with candidates and makes your job posts stand out. It takes about 2 minutes &mdash; and you can finish anytime.
          </p>

          <ol className="mt-10 space-y-1.5">
            {STEPS.map((step, i) => {
              const done = tabCompletion[i];
              const current = i === activeTab;
              const Icon = step.icon;
              return (
                <li key={step.label}>
                  <button
                    type="button"
                    onClick={() => handleTabChange(i)}
                    className={cn(
                      'group flex w-full items-start gap-4 rounded-xl p-3 text-left transition-colors',
                      current ? 'bg-white/15' : 'hover:bg-white/10',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-all',
                        done
                          ? 'border-white bg-white text-primary'
                          : current
                          ? 'border-white bg-white/20 text-white'
                          : 'border-white/40 text-white/70',
                      )}
                    >
                      {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </span>
                    <span className="min-w-0 pt-1">
                      <span className="flex items-center gap-2">
                        <span className={cn('text-sm font-semibold', current || done ? 'text-white' : 'text-white/80')}>
                          {step.label}
                        </span>
                        {step.optional && (
                          <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-medium text-white/70">
                            Optional
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block text-xs text-white/55">{step.description}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="relative z-10 space-y-2 text-xs text-primary-foreground/70">
          <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5" /> Free to start &mdash; no card required</div>
          <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5" /> Your data stays private &amp; secure</div>
        </div>
      </aside>

      {/* ─── Right: form ─── */}
      <main className="flex min-h-screen flex-1 flex-col">
        <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-border px-5 md:px-10">
          <span className="text-sm font-semibold text-foreground lg:hidden">Company setup</span>
          <div className="ml-auto flex items-center gap-4">
            <span className="hidden text-xs text-muted-foreground sm:inline">
              Step {activeTab + 1} of {STEPS.length} &middot; {progress}% complete
            </span>
            <Button variant="ghost" size="sm" onClick={handleFinishLater} className="text-muted-foreground hover:text-foreground">
              I&rsquo;ll finish later
            </Button>
          </div>
        </header>

        {/* mobile progress */}
        <div className="flex-shrink-0 px-5 pt-4 md:px-10 lg:hidden">
          <Progress value={progress} className="h-1.5" />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6 md:px-10">
          <div className="mx-auto max-w-2xl">
            <div className="mb-5">
              <div className="mb-2 flex items-center gap-2">
                <span className="inline-flex h-6 items-center rounded-full bg-primary/10 px-2.5 text-xs font-medium text-primary">
                  Step {activeTab + 1} of {STEPS.length}
                </span>
                {STEPS[activeTab].optional && (
                  <span className="text-xs font-medium text-muted-foreground">&middot; Optional</span>
                )}
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground">{STEPS[activeTab].heading}</h2>
              <p className="mt-1.5 text-sm text-muted-foreground">{STEPS[activeTab].subtext}</p>
            </div>

            <div className="space-y-5">{renderTabContent()}</div>

            {errorMessage && (
              <Alert variant="destructive" className="mt-5">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            {successMessage && (
              <Alert variant="success" className="mt-5">
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* sticky footer nav */}
        <footer className="flex-shrink-0 border-t border-border bg-background/80 px-5 py-4 backdrop-blur md:px-10">
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevious}
              className={cn('text-muted-foreground hover:text-foreground', activeTab === 0 && 'invisible')}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <div className="flex items-center gap-2">
              {!isLast && (
                <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground hover:text-foreground">
                  Skip for now
                </Button>
              )}
              <Button onClick={handleSaveAndNext} disabled={isLoading} size="sm" className="min-w-[160px]">
                {isLoading ? 'Saving…' : isLast ? 'Finish setup' : 'Save & continue'}
                {!isLoading && <ArrowRight className="ml-1.5 h-4 w-4" />}
              </Button>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default CompanySetup;
